import { useToast } from '@app/core/notifications';
import { streamerBoxStreamerBoxDevicesRename } from '@mi-front/api-v2';
import { Device } from '@mi-front/core';
import { FormModal, ModalController, TextInput, useAsyncViewCtx } from '@mi-front/ui';
import { Edit as EditIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

interface Props {
  readonly device: Device;
  readonly rename: (name: string) => Promise<void>;
}

/** Modal UI to rename a streamer-box device. */
export function DeviceRenameP(p: Props) {
  const { device, rename } = p;
  const { $t } = useIntl();
  const { notifySuccess } = useToast();

  return (
    <ModalController
      trigger={(open) => (
        <Button onClick={open} size="small" startIcon={<EditIcon />}>
          {$t({
            id: 'streamerBox.viewScreen.actions.rename.btn',
            defaultMessage: 'Renombrar',
          })}
        </Button>
      )}
    >
      {({ show, onHide }) => (
        <FormModal
          confirmCloseIfDirty={false}
          show={show}
          onHide={onHide}
          title={
            <>
              <EditIcon />
              <FormattedMessage
                id="streamerBox.viewScreen.actions.rename.title"
                defaultMessage="Renombrar dispositivo"
              />
            </>
          }
          initialValues={{
            name: device.name,
          }}
          onSubmit={async ({ name }) => {
            await rename(name);
            notifySuccess(
              $t({
                id: 'streamerBox.viewScreen.actions.rename.done',
                defaultMessage: 'Dispositivo renombrado',
              }),
            );
          }}
          validationSchema={yup.object().shape({
            name: yup.string().required(),
          })}
        >
          {(fp) => (
            <TextInput
              onChange={fp.handleChange}
              onBlur={fp.handleBlur}
              autoFocus
              required
              name="name"
              value={fp.values.name}
              label={
                <FormattedMessage
                  id="streamerBox.viewScreen.actions.rename.label"
                  defaultMessage="Nuevo nombre"
                />
              }
            />
          )}
        </FormModal>
      )}
    </ModalController>
  );
}

/** Renames a streamer-box device and reloads the parent AsyncView. */
export function DeviceRename(p: { device: Device }) {
  const { reload } = useAsyncViewCtx();

  return (
    <DeviceRenameP
      device={p.device}
      rename={async (name) => {
        await streamerBoxStreamerBoxDevicesRename(String(p.device.id), { name });
        reload();
      }}
    />
  );
}
